import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CompactActionBar from "../components/CompactActionBar";
import { useAuth } from "../contexts/AuthContext";
import { useHiddenMode } from "../contexts/HiddenModeContext";
import { cancelIncomeService } from "../services/cancelIncomeService";
import { cancelReceivableIncomeService } from "../services/cancelReceivableIncomeService";
import { incomeService } from "../services/incomeService";
import { hiddenIncomeService } from "../services/hiddenIncomeService";
import "../css/bookingSummary.css";
import "../css/standard-components.css";
import { usePageTitle } from "../hooks/usePageTitle";
import { getToken } from "../utils/authStorage";

interface Project {
  id: number;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  _count: {
    units: number;
  };
}

interface CancelSummaryData {
  id: number;
  bookingId: string;
  unitRef: string;
  customerName: string;
  contactNumber: string;
  saleDeedReceived: number;
  givenCancelAmount: number;
  pendingCancelAmount: number;
  hiddenIncomeReceived: number;
  hiddenRefundGiven: number;
  hiddenRefundPending: number;
  bookingDate: string;
  cancellationDate: string;
  cancellationReason: string;
  status: string;
}

const CancelSummary: React.FC = () => {
  usePageTitle();
  const { user } = useAuth();
  const { isHiddenModeActive } = useHiddenMode();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [bookings, setBookings] = useState<CancelSummaryData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Filter state
  const [customerNameFilter, setCustomerNameFilter] = useState("");
  const [mobileFilter, setMobileFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");

  // Modal state for transaction history
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [transactionHistory, setTransactionHistory] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'cancelIncome' | 'cancelReceivable'>('cancelIncome');
  const [shouldLoadData, setShouldLoadData] = useState(false);
  const [currentViewingBookingId, setCurrentViewingBookingId] = useState<number | null>(null);

  // ── "Add found income" (post-cancellation entry) ──
  const emptyAf = {
    amount: '', paymentDate: '', referenceNumber: '', bankName: '',
    instrumentDate: '', chequeReceivedDate: '', receiptNo: '',
    incomeType: 'other_work', incomeDate: '', reason: '',
  };
  const [showAddFound, setShowAddFound] = useState(false);
  const [addFoundType, setAddFoundType] = useState<'cheque' | 'receivable'>('cheque');
  const [addFoundSubmitting, setAddFoundSubmitting] = useState(false);
  const [addFoundError, setAddFoundError] = useState<string | null>(null);
  const [af, setAf] = useState({ ...emptyAf });
  const afSet = (k: string, v: string) => setAf((prev) => ({ ...prev, [k]: v }));

  const submitAddFound = async () => {
    if (!currentViewingBookingId) return;
    setAddFoundError(null);
    try {
      setAddFoundSubmitting(true);
      if (addFoundType === 'cheque') {
        if (!af.amount || !af.paymentDate || !af.referenceNumber.trim() || !af.bankName.trim() || !af.reason.trim()) {
          setAddFoundError('Amount, payment date, cheque no, bank and reason are required.');
          setAddFoundSubmitting(false);
          return;
        }
        await incomeService.createPostCancellationIncome({
          bookingId: currentViewingBookingId,
          amount: parseFloat(af.amount),
          paymentDate: af.paymentDate,
          referenceNumber: af.referenceNumber.trim(),
          bankName: af.bankName.trim(),
          instrumentDate: af.instrumentDate || undefined,
          chequeReceivedDate: af.chequeReceivedDate || undefined,
          receiptNo: af.receiptNo || undefined,
          reason: af.reason.trim(),
        });
      } else {
        if (!af.amount || !af.incomeDate || !af.reason.trim()) {
          setAddFoundError('Amount, date and reason are required.');
          setAddFoundSubmitting(false);
          return;
        }
        await hiddenIncomeService.createHiddenIncome({
          bookingId: String(currentViewingBookingId),
          incomeType: af.incomeType,
          amount: String(parseFloat(af.amount)),
          collectedAmount: String(parseFloat(af.amount)),
          description: `Post-cancellation entry: ${af.reason.trim()}`,
          incomeDate: af.incomeDate,
        });
      }
      setShowAddFound(false);
      setAf({ ...emptyAf });
      if (currentViewingBookingId) handleViewDetails(currentViewingBookingId);
    } catch (e: any) {
      setAddFoundError(
        e?.response?.data?.error || e?.response?.data?.message || 'Failed to add entry.'
      );
    } finally {
      setAddFoundSubmitting(false);
    }
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        setShowTransactionModal(false);
      }
    };

    if (showTransactionModal) {
      document.addEventListener('keydown', handleEscKey, { capture: true });
      return () => {
        document.removeEventListener('keydown', handleEscKey, { capture: true });
      };
    }
  }, [showTransactionModal]);

  // Redirect if not in hidden mode
  useEffect(() => {
    if (!isHiddenModeActive) {
      window.location.href = "/dashboard";
      return;
    }
  }, [isHiddenModeActive]);

  // Fetch projects on component mount
  useEffect(() => {
    if (isHiddenModeActive) {
      fetchProjects();
    }
  }, [isHiddenModeActive]);

  // Auto-select first project when projects are loaded
  useEffect(() => {
    if (projects.length > 0 && !selectedProject) {
      setSelectedProject(projects[0].id.toString());
    }
  }, [projects]);

  // Fetch bookings when load button is clicked or page changes
  useEffect(() => {
    if (selectedProject && isHiddenModeActive && shouldLoadData) {
      fetchCancelSummary();
    }
  }, [shouldLoadData, page, isHiddenModeActive]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const API_BASE_URL =
        process.env.REACT_APP_API_URL || "http://localhost:5010/api";
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/projects`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch projects");
      }

      const data = await response.json();
      setProjects(data || []);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setError("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchCancelSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const API_BASE_URL =
        process.env.REACT_APP_API_URL || "http://localhost:5010/api";
      const token = getToken();

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (customerNameFilter.trim()) {
        params.append("customerName", customerNameFilter.trim());
      }
      if (mobileFilter.trim()) {
        params.append("mobile", mobileFilter.trim());
      }
      if (unitFilter.trim()) {
        params.append("unit", unitFilter.trim());
      }

      const response = await fetch(
        `${API_BASE_URL}/cancel-summary/project/${selectedProject}?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "x-hidden-mode": "true",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch cancel summary");
      }

      const data = await response.json();
      setBookings(
        Array.isArray(data)
          ? data
          : data.success && Array.isArray(data.data)
          ? data.data
          : []
      );

      if (data.pagination) {
        setTotalPages(data.pagination.pages);
        setTotalCount(data.pagination.total);
      }
    } catch (error) {
      console.error("Error fetching cancel summary:", error);
      setError("Failed to load cancel summary");
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setPage(1);
    fetchCancelSummary();
  };

  const handleClearFilters = () => {
    setCustomerNameFilter("");
    setMobileFilter("");
    setUnitFilter("");
    setPage(1);
    setTimeout(() => fetchCancelSummary(), 0);
  };

  const handleLoadData = () => {
    setPage(1);
    setShouldLoadData(true);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatCurrencyWithDecimals = (amount: number): string => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const handleViewDetails = async (bookingId: number) => {
    try {
      setLoading(true);
      const API_BASE_URL =
        process.env.REACT_APP_API_URL || "http://localhost:5010/api";
      const token = getToken();

      const response = await fetch(
        `${API_BASE_URL}/cancel-summary/booking/${bookingId}/transactions`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "x-hidden-mode": "true",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch cancel transactions");
      }

      const result = await response.json();
      if (result.success) {
        setTransactionHistory(result.data);
        setCurrentViewingBookingId(bookingId);
        setShowTransactionModal(true);
      }
    } catch (error) {
      console.error("Error loading cancel transaction history:", error);
      setError("Failed to load cancel transaction history");
    } finally {
      setLoading(false);
    }
  };

  // Show unauthorized if not in hidden mode
  if (!isHiddenModeActive) {
    return (
      <div className="unauthorized-access">
        <div className="unauthorized-content">
          <h2>🔒 Access Restricted</h2>
          <p>This page is only accessible in hidden mode.</p>
          <p>Please activate hidden mode to view cancel summary.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ background: '#fff' }}>
      {/* Compact Action Bar */}
      <CompactActionBar
        leftActions={
          <>
            <select
              value={selectedProject}
              onChange={(e) => {
                setSelectedProject(e.target.value);
                setShouldLoadData(false);
                setCustomerNameFilter("");
                setMobileFilter("");
                setUnitFilter("");
              }}
              style={{ 
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                minWidth: '200px'
              }}
              disabled={loading}
            >
              <option value="">-- Select Project --</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name} ({project._count.units} units)
                </option>
              ))}
            </select>
            {selectedProject && (
              <button
                onClick={handleLoadData}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  background: loading ? '#9ca3af' : '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: '500'
                }}
              >
                Load Data
              </button>
            )}
          </>
        }
        rightActions={<></>}
      />

      {/* Filters */}
      {selectedProject && (
        <div style={{ padding: '16px 20px', background: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>
                Unit
              </label>
              <input
                type="text"
                value={unitFilter}
                onChange={(e) => setUnitFilter(e.target.value)}
                placeholder="G-301 (wing+unit) or 301 (all wings)"
                onKeyPress={(e) => e.key === "Enter" && handleApplyFilters()}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>
                Customer Name
              </label>
              <input
                type="text"
                value={customerNameFilter}
                onChange={(e) => setCustomerNameFilter(e.target.value)}
                placeholder="Search by customer name..."
                onKeyPress={(e) => e.key === "Enter" && handleApplyFilters()}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>
                Mobile Number
              </label>
              <input
                type="text"
                value={mobileFilter}
                onChange={(e) => setMobileFilter(e.target.value)}
                placeholder="Search by mobile..."
                onKeyPress={(e) => e.key === "Enter" && handleApplyFilters()}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
              <button 
                onClick={handleApplyFilters}
                style={{
                  padding: '8px 16px',
                  background: '#4f46e5',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Apply Filters
              </button>
              <button 
                onClick={handleClearFilters}
                style={{
                  padding: '8px 16px',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div style={{ background: '#fff', margin: 0, padding: 0 }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            Loading cancel summary...
          </div>
        )}

        {error && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#dc2626' }}>
            <p>❌ {error}</p>
            <button
              onClick={() =>
                selectedProject ? fetchCancelSummary() : fetchProjects()
              }
              style={{
                padding: '8px 16px',
                background: '#4f46e5',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Cancel Summary Table */}
        {!loading && !error && selectedProject && bookings.length > 0 && (
          <>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>
                  Showing {bookings.length} of {totalCount} cancelled bookings
                </span>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>
                  Page {page} of {totalPages}
                </span>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="standard-table">
                <thead>
                  <tr>
                    <th style={{ width: '60px', textAlign: 'center' }}>Sr No</th>
                    <th style={{ width: '100px' }}>Unit</th>
                    <th style={{ minWidth: '150px' }}>Customer Name</th>
                    <th style={{ width: '120px' }}>Contact No</th>
                    <th style={{ width: '130px', textAlign: 'right' }}>SD Received</th>
                    <th style={{ width: '130px', textAlign: 'right' }}>Cancel Given</th>
                    <th style={{ width: '130px', textAlign: 'right' }}>Cancel Pending</th>
                    <th style={{ width: '140px', textAlign: 'right' }}>Receivable Received</th>
                    <th style={{ width: '140px', textAlign: 'right' }}>Receivable Given</th>
                    <th style={{ width: '140px', textAlign: 'right' }}>Receivable Pending</th>
                    <th style={{ width: '110px' }}>Cancel Date</th>
                    <th style={{ width: '80px', textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking, index) => {
                    const reverseSrNo = totalCount - (page - 1) * limit - index;
                    return (
                      <tr key={booking.id} style={{ cursor: 'pointer' }} onClick={() => handleViewDetails(booking.id)}>
                        <td style={{ textAlign: 'center', fontWeight: '500' }}>{reverseSrNo}</td>
                        <td style={{ fontWeight: '600', color: '#4f46e5' }}>{booking.unitRef}</td>
                        <td>{booking.customerName}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>{booking.contactNumber}</td>
                        <td style={{ textAlign: 'right', fontWeight: '600', color: '#059669', background: '#f0f4ff' }}>
                          {formatCurrency(booking.saleDeedReceived)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: '600', color: booking.givenCancelAmount > 0 ? '#059669' : '#6b7280', background: '#f0fdf4' }}>
                          {formatCurrency(booking.givenCancelAmount)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: '600', color: booking.pendingCancelAmount > 0 ? '#dc2626' : '#059669', background: '#fef2f2' }}>
                          {formatCurrency(booking.pendingCancelAmount)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: '600', color: '#059669', background: '#f0f4ff' }}>
                          {formatCurrencyWithDecimals(booking.hiddenIncomeReceived)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: '600', color: booking.hiddenRefundGiven > 0 ? '#059669' : '#6b7280', background: '#f0fdf4' }}>
                          {formatCurrencyWithDecimals(booking.hiddenRefundGiven)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: '600', color: booking.hiddenRefundPending > 0 ? '#dc2626' : '#059669', background: '#fef2f2' }}>
                          {formatCurrencyWithDecimals(booking.hiddenRefundPending)}
                        </td>
                        <td style={{ fontSize: '13px', color: '#6b7280' }}>
                          {formatDate(booking.cancellationDate)}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetails(booking.id);
                            }}
                            style={{
                              padding: '6px 12px',
                              background: '#dc2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '13px',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ 
                padding: '16px 20px',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '16px'
              }}>
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  style={{
                    padding: '8px 16px',
                    background: page === 1 ? '#e5e7eb' : '#4f46e5',
                    color: page === 1 ? '#9ca3af' : 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Previous
                </button>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                  style={{
                    padding: '8px 16px',
                    background: page >= totalPages ? '#e5e7eb' : '#4f46e5',
                    color: page >= totalPages ? '#9ca3af' : 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* No bookings message */}
        {!loading && !error && selectedProject && bookings.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
            <p style={{ fontSize: '16px' }}>📋 No cancelled bookings found for the selected project.</p>
          </div>
        )}
      </div>

      {/* Transaction History Modal - Full Screen */}
      {showTransactionModal && transactionHistory && (
        <div className="modal-overlay fullscreen-modal-overlay">
          <div className="modal-content fullscreen-modal-content">
            <div className="modal-header fullscreen-modal-header">
              <h2>Cancel Transaction History</h2>
              <button
                onClick={() => setShowTransactionModal(false)}
                className="close-button"
              >
                ×
              </button>
            </div>

            <div className="modal-body fullscreen-modal-body">
              {/* Three Column Layout */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: '20px', 
                marginBottom: '30px',
                background: '#f9fafb',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                {/* Column 1: Booking Information */}
                <div style={{ 
                  background: 'white', 
                  padding: '16px', 
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  <h4 style={{ 
                    margin: '0 0 16px 0', 
                    fontSize: '15px', 
                    fontWeight: '600',
                    color: '#1f2937',
                    borderBottom: '2px solid #dc2626',
                    paddingBottom: '8px'
                  }}>📋 Booking Information</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div>
                      <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Unit Number</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#059669' }}>
                        {transactionHistory.booking.unitRef}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Booking Date</div>
                      <div style={{ fontSize: '13px', color: '#374151' }}>
                        {formatDate(transactionHistory.booking.bookingDate)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Cancellation Date</div>
                      <div style={{ fontSize: '13px', color: '#dc2626', fontWeight: '600' }}>
                        {formatDate(transactionHistory.booking.cancellationDate)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Cancellation Reason</div>
                      <div style={{ fontSize: '12px', color: '#6b7280', fontStyle: transactionHistory.booking.cancellationReason ? 'normal' : 'italic' }}>
                        {transactionHistory.booking.cancellationReason || 'No reason provided'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Status</div>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: '#fee2e2',
                        color: '#991b1b'
                      }}>
                        Cancelled
                      </span>
                    </div>
                  </div>
                </div>

                {/* Column 2: Customer Information */}
                <div style={{ 
                  background: 'white', 
                  padding: '16px', 
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  <h4 style={{ 
                    margin: '0 0 16px 0', 
                    fontSize: '15px', 
                    fontWeight: '600',
                    color: '#1f2937',
                    borderBottom: '2px solid #dc2626',
                    paddingBottom: '8px'
                  }}>👥 Customers</h4>
                  {transactionHistory.booking.applicants && transactionHistory.booking.applicants.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {transactionHistory.booking.applicants.map((applicant: any) => (
                        <div key={`applicant-${applicant.id}`} style={{ 
                          padding: '10px', 
                          borderRadius: '6px',
                          background: applicant.isPrimary ? '#fef2f2' : '#f9fafb',
                          border: applicant.isPrimary ? '1px solid #fecaca' : '1px solid #e5e7eb'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: applicant.isPrimary ? '#dc2626' : '#374151' }}>
                              {applicant.firstName} {applicant.surname}
                            </div>
                            {applicant.isPrimary && (
                              <span style={{ 
                                fontSize: '10px', fontWeight: '600', color: '#fff', 
                                background: '#dc2626', padding: '2px 6px', borderRadius: '4px' 
                              }}>PRIMARY</span>
                            )}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '3px' }}>
                            📞 {applicant.contactNumber || 'No contact'}
                          </div>
                          {applicant.panCard && (
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '3px' }}>
                              PAN: <span style={{ fontFamily: 'monospace', fontWeight: '500', color: '#374151' }}>{applicant.panCard}</span>
                            </div>
                          )}
                          {applicant.aadharCard && (
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                              Aadhar: <span style={{ fontFamily: 'monospace', fontWeight: '500', color: '#374151' }}>{applicant.aadharCard}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {transactionHistory.booking.customerName || 'No customer information'}
                    </div>
                  )}
                </div>

                {/* Column 3: Financial Summary */}
                <div style={{ 
                  background: 'white', 
                  padding: '16px', 
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  <h4 style={{ 
                    margin: '0 0 16px 0', 
                    fontSize: '15px', 
                    fontWeight: '600',
                    color: '#1f2937',
                    borderBottom: '2px solid #dc2626',
                    paddingBottom: '8px'
                  }}>💰 Cancel Financial Summary</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {/* Cancel Income Section (Sale Deed Refunds) */}
                    <div style={{ background: '#fef2f2', padding: '8px', borderRadius: '4px' }}>
                      <div style={{ fontSize: '11px', color: '#dc2626', fontWeight: '600', marginBottom: '6px' }}>
                        CANCEL INCOME (Sale Deed)
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '3px' }}>
                        <span style={{ color: '#6b7280' }}>SD Received:</span>
                        <span style={{ fontWeight: '600', color: '#374151' }}>
                          {formatCurrency(transactionHistory.cancelIncome.saleDeedReceived)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '3px' }}>
                        <span style={{ color: '#6b7280' }}>Refunded:</span>
                        <span style={{ fontWeight: '600', color: '#059669' }}>
                          {formatCurrency(transactionHistory.cancelIncome.totalRefunded)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                        <span style={{ color: '#6b7280' }}>Pending:</span>
                        <span style={{ fontWeight: '600', color: '#dc2626' }}>
                          {formatCurrency(transactionHistory.cancelIncome.pendingRefund)}
                        </span>
                      </div>
                    </div>

                    {/* Cancel Receivable Section (Hidden Income Refunds) */}
                    <div style={{ background: '#fff7ed', padding: '8px', borderRadius: '4px' }}>
                      <div style={{ fontSize: '11px', color: '#c2410c', fontWeight: '600', marginBottom: '6px' }}>
                        CANCEL RECEIVABLE (Hidden)
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '3px' }}>
                        <span style={{ color: '#6b7280' }}>Receivable Received:</span>
                        <span style={{ fontWeight: '600', color: '#374151' }}>
                          {formatCurrencyWithDecimals(transactionHistory.cancelReceivable.hiddenIncomeReceived)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '3px' }}>
                        <span style={{ color: '#6b7280' }}>Refunded:</span>
                        <span style={{ fontWeight: '600', color: '#059669' }}>
                          {formatCurrencyWithDecimals(transactionHistory.cancelReceivable.totalRefunded)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                        <span style={{ color: '#6b7280' }}>Pending:</span>
                        <span style={{ fontWeight: '600', color: '#dc2626' }}>
                          {formatCurrencyWithDecimals(transactionHistory.cancelReceivable.pendingRefund)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Add found income (post-cancellation entry) */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '4px 0 12px' }}>
                <button
                  onClick={() => { setShowAddFound((v) => !v); setAddFoundError(null); }}
                  style={{ padding: '8px 14px', border: '1px solid #059669', background: showAddFound ? '#059669' : 'white', color: showAddFound ? 'white' : '#059669', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                >
                  ➕ Add Found Income
                </button>
              </div>
              {showAddFound && (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 18, background: '#f9fafb' }}>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                    Record income discovered <strong>after</strong> this booking was cancelled. It is saved as an approved, received entry against this cancelled booking and appears in the tabs below.
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                    <button onClick={() => setAddFoundType('cheque')} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: addFoundType === 'cheque' ? '#111827' : 'white', color: addFoundType === 'cheque' ? 'white' : '#374151' }}>Sale-Deed (Cheque)</button>
                    <button onClick={() => setAddFoundType('receivable')} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: addFoundType === 'receivable' ? '#111827' : 'white', color: addFoundType === 'receivable' ? 'white' : '#374151' }}>Receivable</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 10 }}>
                    {addFoundType === 'cheque' ? (
                      <>
                        <label style={{ fontSize: 12 }}>Amount *<input type="number" value={af.amount} onChange={(e) => afSet('amount', e.target.value)} style={{ width: '100%', padding: 6 }} /></label>
                        <label style={{ fontSize: 12 }}>Payment date *<input type="date" value={af.paymentDate} onChange={(e) => afSet('paymentDate', e.target.value)} style={{ width: '100%', padding: 6 }} /></label>
                        <label style={{ fontSize: 12 }}>Cheque no *<input value={af.referenceNumber} onChange={(e) => afSet('referenceNumber', e.target.value)} style={{ width: '100%', padding: 6 }} /></label>
                        <label style={{ fontSize: 12 }}>Bank *<input value={af.bankName} onChange={(e) => afSet('bankName', e.target.value)} style={{ width: '100%', padding: 6 }} /></label>
                        <label style={{ fontSize: 12 }}>Instrument date<input type="date" value={af.instrumentDate} onChange={(e) => afSet('instrumentDate', e.target.value)} style={{ width: '100%', padding: 6 }} /></label>
                        <label style={{ fontSize: 12 }}>Received date<input type="date" value={af.chequeReceivedDate} onChange={(e) => afSet('chequeReceivedDate', e.target.value)} style={{ width: '100%', padding: 6 }} /></label>
                        <label style={{ fontSize: 12 }}>Receipt no<input value={af.receiptNo} onChange={(e) => afSet('receiptNo', e.target.value)} style={{ width: '100%', padding: 6 }} /></label>
                      </>
                    ) : (
                      <>
                        <label style={{ fontSize: 12 }}>Income type *
                          <select value={af.incomeType} onChange={(e) => afSet('incomeType', e.target.value)} style={{ width: '100%', padding: 6 }}>
                            <option value="other_work">Other work</option>
                            <option value="charges">Charges</option>
                            <option value="terrace">Terrace</option>
                            <option value="banakhat">Banakhat</option>
                            <option value="adani_gas">Adani gas</option>
                          </select>
                        </label>
                        <label style={{ fontSize: 12 }}>Amount *<input type="number" value={af.amount} onChange={(e) => afSet('amount', e.target.value)} style={{ width: '100%', padding: 6 }} /></label>
                        <label style={{ fontSize: 12 }}>Date *<input type="date" value={af.incomeDate} onChange={(e) => afSet('incomeDate', e.target.value)} style={{ width: '100%', padding: 6 }} /></label>
                      </>
                    )}
                    <label style={{ fontSize: 12, gridColumn: '1 / -1' }}>Reason * (why it was found after cancellation)<input value={af.reason} onChange={(e) => afSet('reason', e.target.value)} style={{ width: '100%', padding: 6 }} /></label>
                  </div>
                  {addFoundError && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 10 }}>{addFoundError}</div>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                    <button onClick={submitAddFound} disabled={addFoundSubmitting} style={{ padding: '8px 16px', background: '#059669', color: 'white', border: 'none', borderRadius: 6, cursor: addFoundSubmitting ? 'default' : 'pointer', fontSize: 13, fontWeight: 600, opacity: addFoundSubmitting ? 0.6 : 1 }}>{addFoundSubmitting ? 'Saving…' : 'Save entry'}</button>
                    <button onClick={() => { setShowAddFound(false); setAf({ ...emptyAf }); setAddFoundError(null); }} style={{ padding: '8px 16px', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                  </div>
                </div>
              )}

              {/* Tabs for Cancel Income and Cancel Receivable */}
              <div className="transactions-table-section">
                <div style={{ borderBottom: '2px solid #e5e7eb', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      onClick={() => setActiveTab('cancelIncome')}
                      style={{
                        padding: '12px 24px',
                        border: 'none',
                        background: activeTab === 'cancelIncome' ? '#dc2626' : 'transparent',
                        color: activeTab === 'cancelIncome' ? 'white' : '#6b7280',
                        borderBottom: activeTab === 'cancelIncome' ? '3px solid #dc2626' : '3px solid transparent',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: '600',
                        borderRadius: '8px 8px 0 0',
                        transition: 'all 0.2s'
                      }}
                    >
                      💰 Cancel Income ({(transactionHistory.cancelIncome.transactions.length) + (transactionHistory.cancelIncome.incomeTransactions?.length || 0)})
                    </button>
                    <button
                      onClick={() => setActiveTab('cancelReceivable')}
                      style={{
                        padding: '12px 24px',
                        border: 'none',
                        background: activeTab === 'cancelReceivable' ? '#dc2626' : 'transparent',
                        color: activeTab === 'cancelReceivable' ? 'white' : '#6b7280',
                        borderBottom: activeTab === 'cancelReceivable' ? '3px solid #dc2626' : '3px solid transparent',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: '600',
                        borderRadius: '8px 8px 0 0',
                        transition: 'all 0.2s'
                      }}
                    >
                      📋 Cancel Receivable ({(transactionHistory.cancelReceivable.transactions.length) + (transactionHistory.cancelReceivable.incomeTransactions?.length || 0)})
                    </button>
                  </div>
                </div>

                {/* Cancel Income Tab */}
                {activeTab === 'cancelIncome' && (() => {
                  // Show received sale-deed income AND refunds together, so the
                  // cancel-income tab mirrors the cancel-receivable tab (received
                  // in green, returned/refund in red).
                  const combinedIncome = [
                    ...(transactionHistory.cancelIncome.incomeTransactions || []),
                    ...(transactionHistory.cancelIncome.transactions || []).map((t: any) => ({ ...t, type: 'refund' })),
                  ];
                  return (
                  <div className="transactions-table-container">
                    <table className="transactions-table" style={{ width: '100%' }}>
                      <thead>
                        <tr>
                          <th style={{ width: '60px', textAlign: 'center' }}>Sr. No</th>
                          <th style={{ width: '90px' }}>Type</th>
                          <th style={{ width: '130px', textAlign: 'right' }}>Amount</th>
                          <th style={{ width: '110px' }}>Date</th>
                          <th style={{ minWidth: '140px' }}>Reference No.</th>
                          <th style={{ minWidth: '140px' }}>Remarks</th>
                          <th style={{ width: '120px' }}>Processed By</th>
                          {user?.isSuperAdmin && <th style={{ width: '50px', textAlign: 'center' }}>Action</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {combinedIncome.length > 0 ? (
                          combinedIncome.map((txn: any, index: number) => (
                            <tr key={`cancel-income-${txn.type}-${txn.id}-${index}`} style={{ background: txn.type === 'refund' ? '#fef2f2' : undefined }}>
                              <td style={{ textAlign: 'center', fontWeight: '600' }}>
                                {index + 1}
                              </td>
                              <td>
                                <span style={{
                                  padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700',
                                  background: txn.type === 'refund' ? '#fee2e2' : '#dcfce7',
                                  color: txn.type === 'refund' ? '#991b1b' : '#166534',
                                }}>
                                  {txn.type === 'refund' ? 'REFUND' : 'INCOME'}
                                </span>
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: '600', color: txn.type === 'refund' ? '#dc2626' : '#059669' }}>
                                {txn.type === 'refund' ? '-' : ''}{formatCurrency(Math.abs(txn.amount))}
                              </td>
                              <td style={{ fontSize: '13px' }}>
                                {formatDate(txn.date)}
                              </td>
                              <td style={{ fontSize: '13px', fontFamily: 'monospace' }}>
                                {txn.referenceNumber || '-'}
                              </td>
                              <td style={{ fontSize: '13px', color: '#6b7280' }}>
                                {txn.remarks || '-'}
                              </td>
                              <td style={{ fontSize: '13px' }}>
                                {txn.processedBy || '-'}
                              </td>
                              {user?.isSuperAdmin && (
                                <td style={{ textAlign: 'center' }}>
                                  {txn.type === 'refund' ? (
                                  <button
                                    onClick={async () => {
                                      if (!window.confirm(`Are you sure you want to delete this cancel income transaction of ${formatCurrency(Math.abs(txn.amount))}?`)) return;
                                      try {
                                        await cancelIncomeService.deleteTransaction(txn.id);
                                        if (currentViewingBookingId) handleViewDetails(currentViewingBookingId);
                                      } catch (err) {
                                        alert('Failed to delete transaction');
                                      }
                                    }}
                                    style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '4px', fontSize: '14px' }}
                                    title="Delete transaction"
                                  >
                                    🗑️
                                  </button>
                                  ) : '-'}
                                </td>
                              )}
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={user?.isSuperAdmin ? 8 : 7} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                              No cancel income transactions found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  );
                })()}

                {/* Cancel Receivable Tab */}
                {activeTab === 'cancelReceivable' && (() => {
                  const combinedReceivable = [
                    ...(transactionHistory.cancelReceivable.incomeTransactions || []),
                    ...(transactionHistory.cancelReceivable.transactions || []),
                  ];
                  return (
                    <div className="transactions-table-container">
                      <table className="transactions-table" style={{ width: '100%' }}>
                        <thead>
                          <tr>
                            <th style={{ width: '60px', textAlign: 'center' }}>Sr. No</th>
                            <th style={{ width: '90px', textAlign: 'center' }}>Type</th>
                            <th style={{ width: '130px', textAlign: 'right' }}>Amount</th>
                            <th style={{ width: '110px' }}>Date</th>
                            <th style={{ width: '120px' }}>Payment Method</th>
                            <th style={{ minWidth: '140px' }}>Reference No.</th>
                            <th style={{ minWidth: '140px' }}>Remarks</th>
                            {user?.isSuperAdmin && <th style={{ width: '50px', textAlign: 'center' }}>Action</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {combinedReceivable.length > 0 ? (
                            combinedReceivable.map((txn: any, index: number) => (
                              <tr key={`cr-${txn.id}-${index}`} style={{ background: txn.type === 'refund' ? '#fef2f2' : undefined }}>
                                <td style={{ textAlign: 'center', fontWeight: '600', color: '#dc2626' }}>
                                  {index + 1}
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  <span style={{
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    borderRadius: '10px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    background: txn.type === 'refund' ? '#fee2e2' : '#dcfce7',
                                    color: txn.type === 'refund' ? '#991b1b' : '#166534',
                                  }}>
                                    {txn.type === 'refund' ? 'REFUND' : 'INCOME'}
                                  </span>
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: '600', color: txn.type === 'refund' ? '#dc2626' : '#059669' }}>
                                  {txn.type === 'refund'
                                    ? '-' + formatCurrencyWithDecimals(Math.abs(txn.amount))
                                    : formatCurrencyWithDecimals(txn.amount)
                                  }
                                </td>
                                <td style={{ fontSize: '13px' }}>
                                  {formatDate(txn.date)}
                                </td>
                                <td style={{ fontSize: '13px', textTransform: 'uppercase' }}>
                                  {txn.paymentMethod || '-'}
                                </td>
                                <td style={{ fontSize: '13px', fontFamily: 'monospace' }}>
                                  {txn.referenceNumber || '-'}
                                </td>
                                <td style={{ fontSize: '13px', color: '#6b7280' }}>
                                  {txn.remarks || '-'}
                                </td>
                                {user?.isSuperAdmin && (
                                  <td style={{ textAlign: 'center' }}>
                                    <button
                                      onClick={async () => {
                                        const label = txn.type === 'income' ? 'income record' : 'refund transaction';
                                        if (!window.confirm(`Are you sure you want to delete this ${label} of ${formatCurrencyWithDecimals(Math.abs(txn.amount))}?`)) return;
                                        try {
                                          await cancelReceivableIncomeService.deleteTransaction(txn.id, txn.type === 'income' ? 'income' : 'refund');
                                          if (currentViewingBookingId) handleViewDetails(currentViewingBookingId);
                                        } catch (err) {
                                          alert('Failed to delete transaction');
                                        }
                                      }}
                                      style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '4px', fontSize: '14px' }}
                                      title={`Delete ${txn.type === 'income' ? 'income record' : 'refund transaction'}`}
                                    >
                                      🗑️
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={user?.isSuperAdmin ? 8 : 7} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                                No cancel receivable transactions found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowTransactionModal(false)}
                className="cancel-button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CancelSummary;
